import {
  CalendarAddAlt,
  CheckmarkOutline,
  DeliveryParcel,
  Document,
  Information,
  SearchLocate,
} from "@carbon/icons-react";
import { Edit } from "@mui/icons-material";
import { Button, List, ListItem, ListItemText } from "@mui/material";
import moment from "moment";
import { ReactElement } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";

import { useOrderContext } from "../../../../contexts/OrderContext";
import { useProgression } from "../../../../hooks/useProgression";
import { fileSizeInMb } from "../../../../utils/helper";
import EditInformation from "../editInformation/EditInformation";
import "./OrderSummary.scss";

interface OrderSummaryProps {
  isConfirmation: boolean;
}

const OrderSummary = (props: OrderSummaryProps) => {
  const { t } = useTranslation();
  const { id: idFromUrl } = useParams();
  const { savedProgress } = useOrderContext();
  const { jumpTo } = useProgression();

  const { isConfirmation } = props;
  const { generalInfo, deliveryItems, plannedDelivery, documentItems } =
    savedProgress;

  const isEditOrder = !!idFromUrl;

  const formattedDateString =
    plannedDelivery &&
    `${moment(new Date(plannedDelivery))
      .locale(t("locale"))
      .format(t("dateTimeDayYearFormat"))} `;

  const renderEditInformation = (
    icon: ReactElement,
    header: string,
    body: ReactElement,
    jumpToScreen: number,
  ) => {
    return (
      <div className="page-content-paragraph">
        <EditInformation
          icon={icon}
          text={
            <div>
              <b>{header}</b>
              {body}
            </div>
          }
          actionButton={
            isConfirmation ? undefined : (
              <Button onClick={() => jumpTo(jumpToScreen)}>
                <Edit />
              </Button>
            )
          }
        />
      </div>
    );
  };

  const renderCheckDataSection = () => {
    return isConfirmation ? (
      <div className="summary-check-data mb-5 mt-18">
        <CheckmarkOutline
          className="summary-check-data-icon-success"
          data-testid="summary-check-data-icon-success"
          size={32}
        />
        <div className="summary-check-data-text">
          {isEditOrder ? t("orderEditSuccess") : t("orderCreationSuccess")}
        </div>
      </div>
    ) : (
      <div className="summary-check-data mb-5">
        <div className="summary-check-data-icon">
          <SearchLocate size={24} />
        </div>
        <div className="summary-check-data-text">{t("checkData")}</div>
      </div>
    );
  };

  return (
    <div data-testid="summaryContent" className="page-content">
      <div>
        {renderCheckDataSection()}
        <div className="page-content-divider" />
        {renderEditInformation(
          <Information size={24} />,
          `${t("enterInfo")}`,
          <div>
            {`${generalInfo?.deliveryNumber} | ${generalInfo?.documentNumber && generalInfo.documentNumber + " | "} ${t(generalInfo?.documentType || "")} | ${generalInfo?.location}`}
          </div>,
          1,
        )}
        <div className="page-content-divider" />
        {renderEditInformation(
          <DeliveryParcel size={24} />,
          `${t("enterItems")} - ${deliveryItems?.length} ${deliveryItems && deliveryItems?.length > 1 ? t("items") : t("item")}`,
          <List>
            {deliveryItems?.map((item) => (
              <ListItem key={item.id} className="summary-list-item">
                <ListItemText
                  primary={`${item.position} - ${item.material} - ${item.stockType} `}
                  secondary={`${t("quantity")}: ${item.quantity} ${item.unitOfMeasurement}`}
                />
              </ListItem>
            ))}
          </List>,
          2,
        )}
        <div className="page-content-divider" />
        {renderEditInformation(
          <CalendarAddAlt size={24} />,
          `${t("enterTimes")}`,
          <div>{`${t("plannedDelivery")}: ${formattedDateString}`}</div>,
          3,
        )}
        <div className="page-content-divider" />
        {renderEditInformation(
          <Document size={24} />,
          `${t("documents")} - ${documentItems?.length} ${documentItems && documentItems?.length > 1 ? t("items") : t("item")}`,
          <List>
            {documentItems?.map((item) => (
              <ListItem key={item.id} className="summary-list-item">
                <ListItemText
                  primary={`${item.name} - ${t(item.documentType || "")}`}
                  secondary={
                    item.file &&
                    `${item.file?.name} - ${item.file && fileSizeInMb(item.file)}`
                  }
                />
              </ListItem>
            ))}
          </List>,
          4,
        )}
      </div>
    </div>
  );
};

export default OrderSummary;
